import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TableCell,
    TableRow,
    LinearProgress,
    Alert,
    Snackbar,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Autocomplete,
    TextField
} from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { Sync as SyncIcon, Visibility as VisibilityIcon, PhotoCamera as PhotoCameraIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { syncProducts, getProducts, getSyncStatus, toggleBestSeller, uploadProductImages, type Product as ProductType } from '../../../shared/services/productService';
import { resolveImageUrl } from '../../../shared/utils/imageUrl';
import { Loader } from '../../../shared/components/Loader';
import { ProductImage } from '../../../shared/components/ProductImage';
import { SyncProgressBanner, type SyncProgressDetails } from '../../../shared/components/SyncProgressBanner';
import { ImageUploadValidationDialog } from './ImageUploadValidationDialog';

export const Product: React.FC = () => {
    const SYNC_POLL_INTERVAL_MS = 5000;
    const navigate = useNavigate();
    const [products, setProducts] = useState<ProductType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [syncing, setSyncing] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [imageStatus, setImageStatus] = useState<string>('All');
    const [pagination, setPagination] = useState<{
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
    } | null>(null);
    const [syncDetails, setSyncDetails] = useState<SyncProgressDetails | null>(null);
    const [toggling] = useState<number | string | null>(null);
    const [uploadingProductId, setUploadingProductId] = useState<number | string | null>(null);
    const syncPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const syncPollInFlightRef = useRef<boolean>(false);
    const imageUploadRef = useRef<HTMLInputElement>(null);
    const uploadTargetRef = useRef<ProductType | null>(null);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [targetProduct, setTargetProduct] = useState<ProductType | null>(null);

    const clearSyncPolling = useCallback(() => {
        if (syncPollTimeoutRef.current) {
            clearTimeout(syncPollTimeoutRef.current);
            syncPollTimeoutRef.current = null;
        }
        syncPollInFlightRef.current = false;
    }, []);

    const loadProducts = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getProducts({
                page: 1,
                limit: 25000 // Ensure we fetch all products (user has 12,000+ products)
            });
            const responseData = response?.data;
            const data = responseData && !Array.isArray(responseData) && 'items' in responseData
                ? responseData.items
                : (Array.isArray(responseData) ? responseData : []);
            setProducts(data);
        } catch (err: any) {
            console.error('Error fetching products:', err);
            setError('Failed to load products');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    useEffect(() => {
        return () => {
            clearSyncPolling();
        };
    }, [clearSyncPolling]);

    const categories = React.useMemo(() => {
        const uniqueCategories = Array.from(new Set(products.map(p => p.categoryName).filter(Boolean)));
        return uniqueCategories.sort() as string[];
    }, [products]);

    const filteredProducts = React.useMemo(() => {
        let result = products;

        if (selectedCategory && selectedCategory !== 'All') {
            result = result.filter(product => product.categoryName === selectedCategory);
        }

        if (imageStatus === 'Has Image') {
            result = result.filter(product => !!resolveImageUrl(product.imageUrl));
        } else if (imageStatus === 'No Image') {
            result = result.filter(product => !resolveImageUrl(product.imageUrl));
        }

        if (searchInput.trim()) {
            const q = searchInput.trim().toLowerCase();
            result = result.filter(product =>
                (product.name?.toLowerCase() || '').includes(q) ||
                (product.categoryName?.toLowerCase() || '').includes(q)
            );
        }

        return result;
    }, [products, searchInput, selectedCategory, imageStatus]);

    const paginatedProducts = React.useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredProducts.slice(start, start + pageSize);
    }, [filteredProducts, currentPage, pageSize]);

    const handleSync = async () => {
        const pollStatus = async (): Promise<void> => {
            if (syncPollInFlightRef.current) return;
            syncPollInFlightRef.current = true;

            try {
                const statusRes = await getSyncStatus();
                if (!statusRes.success) {
                    setError('Unable to fetch sync status');
                    setSyncing(false);
                    clearSyncPolling();
                    return;
                }

                setSyncDetails(statusRes.data as any);

                if (statusRes.data.isRunning) {
                    syncPollTimeoutRef.current = setTimeout(pollStatus, SYNC_POLL_INTERVAL_MS);
                    return;
                }

                setSyncing(false);
                clearSyncPolling();

                if ((statusRes.data as any).lastError) {
                    setError((statusRes.data as any).lastError);
                } else {
                    setSuccess('Products synced successfully');
                }
                await loadProducts();
            } catch (err: any) {
                const statusCode = err?.response?.status;
                const message = err?.response?.data?.message;
                if (statusCode === 502 || statusCode === 504) {
                    // Transient proxy/backend timeout: retry with backoff-like delay.
                    syncPollTimeoutRef.current = setTimeout(pollStatus, SYNC_POLL_INTERVAL_MS * 2);
                } else {
                    setError(message || 'Failed to check sync status');
                    setSyncing(false);
                    clearSyncPolling();
                }
            } finally {
                syncPollInFlightRef.current = false;
            }
        };

        try {
            clearSyncPolling();
            setSyncing(true);
            setError('');
            setSyncDetails(null);

            await syncProducts();
            await pollStatus();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to start sync');
            setSyncing(false);
            clearSyncPolling();
        }
    };

    const handleToggleBestSeller = async (product: ProductType) => {
        const id = product.documentId ?? product.id ?? product.productId;
        if (id == null) return;

        const originalStatus = !!product.isBestSeller;

        try {
            setProducts(prev => prev.map(p => {
                const pid = p.documentId ?? p.id ?? p.productId;
                if (pid === id) {
                    return { ...p, isBestSeller: !originalStatus };
                }
                return p;
            }));

            const response = await toggleBestSeller(id);
            if (response.success) {
                setProducts(prev => prev.map(p => {
                    const pid = p.documentId ?? p.id ?? p.productId;
                    if (pid === id) {
                        return { ...p, isBestSeller: response.data?.isBestSeller ?? !originalStatus };
                    }
                    return p;
                }));
                setSuccess(response.message || 'Status updated successfully');
            } else {
                setProducts(prev => prev.map(p => {
                    const pid = p.documentId ?? p.id ?? p.productId;
                    if (pid === id) {
                        return { ...p, isBestSeller: originalStatus };
                    }
                    return p;
                }));
                setError(response.message || 'Failed to update status');
            }
        } catch (err: any) {
            setProducts(prev => prev.map(p => {
                const pid = p.documentId ?? p.id ?? p.productId;
                if (pid === id) {
                    return { ...p, isBestSeller: originalStatus };
                }
                return p;
            }));
            setError(err.response?.data?.message || 'Failed to toggle Best Seller status');
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(1);
    };



    const handleViewProduct = (product: ProductType) => {
        const id = product.documentId ?? product.id ?? product.productId;
        if (id != null) navigate(`/product/view/${id}`);
    };

    const handleUploadImageClick = (product: ProductType) => {
        uploadTargetRef.current = product;
        imageUploadRef.current?.click();
    };

    const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const product = uploadTargetRef.current;
        if (files.length === 0 || !product) return;
        
        setSelectedFiles(files);
        setTargetProduct(product);
        setValidationDialogOpen(true);
    };

    const handleConfirmUpload = async (files: File[], newNames: string[]) => {
        const product = targetProduct;
        if (!files.length || !product) return;

        const id = product.documentId ?? String(product.id ?? product.productId);
        if (!id) return;

        try {
            setUploadingProductId(id);
            setError('');
            const response = await uploadProductImages(String(id), files, newNames);
            if (response.success) {
                setProducts(prev => prev.map(p => {
                    const pid = p.documentId ?? p.id ?? p.productId;
                    if (pid === id) return { ...p, imageUrl: response.data?.imageUrl ?? p.imageUrl };
                    return p;
                }));
                setSuccess(`${files.length > 1 ? 'Images' : 'Image'} uploaded successfully`);
                setValidationDialogOpen(false);
            } else {
                setError(response.message ?? 'Image upload failed');
            }
        } catch {
            setError('Image upload failed');
        } finally {
            setUploadingProductId(null);
            uploadTargetRef.current = null;
            if (imageUploadRef.current) imageUploadRef.current.value = '';
        }
    };

    const totalRows = pagination?.totalItems ?? products.length;
    const totalPages = (pagination?.totalPages ?? Math.ceil(totalRows / pageSize)) || 1;

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Hidden file input for image uploads */}
            <input
                ref={imageUploadRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageFileChange}
            />

            <ImageUploadValidationDialog
                open={validationDialogOpen}
                files={selectedFiles}
                currentProductName={targetProduct?.name ?? ''}
                allProducts={products}
                onClose={() => setValidationDialogOpen(false)}
                onConfirm={handleConfirmUpload}
                uploading={!!uploadingProductId}
            />

            <div className="flex flex-col 2xl:flex-row flex-0 2xl:items-center 2xl:justify-between gap-4 p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Products
                    </h2>
                </div>
                <div className="flex flex-wrap items-center xl:justify-end gap-3 w-full 2xl:w-auto">
                    <Autocomplete
                        size="small"
                        options={['All', ...categories]}
                        value={selectedCategory}
                        onChange={(_, newValue) => {
                            setSelectedCategory(newValue || 'All');
                            setCurrentPage(1);
                        }}
                        disabled={loading}
                        className="flex-auto sm:flex-none min-w-[150px] max-w-[250px]"
                        renderInput={(params) => <TextField {...params} label="Category" variant="outlined" />}
                        disableClearable
                    />
                    <FormControl variant="outlined" size="small" className="flex-auto sm:flex-none min-w-[130px] max-w-[180px]">
                        <InputLabel>Image Status</InputLabel>
                        <Select
                            value={imageStatus}
                            onChange={(e) => {
                                setImageStatus(e.target.value);
                                setCurrentPage(1);
                            }}
                            label="Image Status"
                            disabled={loading}
                        >
                            <MenuItem value="All">All Items</MenuItem>
                            <MenuItem value="Has Image">With Image</MenuItem>
                            <MenuItem value="No Image">Without Image</MenuItem>
                        </Select>
                    </FormControl>
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search products"
                        className="flex-auto sm:flex-none w-full sm:w-[200px]"
                    />
                    <AppButton
                        variant="secondary"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => navigate('/product/bulk-image-upload')}
                        disabled={syncing || loading}
                    >
                        Bulk Image Upload
                    </AppButton>
                    <AppButton
                        variant="primary"
                        startIcon={<SyncIcon />}
                        onClick={handleSync}
                        disabled={syncing || loading}
                        loading={syncing}
                    >
                        {syncing ? 'Syncing...' : 'Sync Products'}
                    </AppButton>
                </div>
            </div>

            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    <SyncProgressBanner syncing={syncing} details={syncDetails} />
                    {loading && products.length > 0 && <LinearProgress className="mb-4" />}
                    {loading && products.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700" width={60}>Image</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Name</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Category</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Price</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">MRP</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Stock</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Created At</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Best Seller</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" width={180}>Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={filteredProducts.length === 0}
                                emptyColSpan={10}
                                emptyMessage="No products match your search."
                            >
                                {paginatedProducts.map((product) => (
                                    <TableRow key={product.productId ?? product.id ?? product.documentId ?? product.name} hover>
                                        <TableCell>
                                            <ProductImage 
                                                imageUrl={product.imageUrl} 
                                                name={product.name} 
                                                size={40} 
                                            />
                                        </TableCell>
                                        <TableCell>{product.name}</TableCell>
                                        <TableCell>{product.categoryName || '-'}</TableCell>
                                        <TableCell align="right">{product.price ?? '-'}</TableCell>
                                        <TableCell align="right">{product.mrp ?? '-'}</TableCell>
                                        <TableCell align="right">{product.stock ?? product.totalStock ?? '-'}</TableCell>
                                        <TableCell>
                                            <span style={{
                                                color: product.status === 'Active' ? 'green' : 'red',
                                                fontWeight: 'bold'
                                            }}>
                                                {product.status || '-'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {product.createdAt
                                                ? new Date(product.createdAt).toLocaleDateString()
                                                : '-'
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={!!product.isBestSeller}
                                                onChange={() => handleToggleBestSeller(product)}
                                                disabled={toggling === (product.documentId ?? product.id ?? product.productId)}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <AppButton
                                                    variant="secondary"
                                                    size="small"
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => handleViewProduct(product)}
                                                >
                                                    View
                                                </AppButton>
                                                <AppButton
                                                    variant="secondary"
                                                    size="small"
                                                    startIcon={<PhotoCameraIcon />}
                                                    onClick={() => handleUploadImageClick(product)}
                                                    disabled={uploadingProductId === (product.documentId ?? product.id ?? product.productId)}
                                                    loading={uploadingProductId === (product.documentId ?? product.id ?? product.productId)}
                                                >
                                                    Image
                                                </AppButton>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={filteredProducts.length}
                                page={currentPage - 1}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(1);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
